import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from '@tanstack/react-router'
import { Search, X, User, ShoppingCart, CreditCard, FileText, Ticket, Loader2 } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { apiClient } from '@/lib/api/client'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { useModal } from '@/components/modals/modal-container'
import { usePermission } from '@/lib/permissions'
import { useEvent } from '@/lib/api/queries'

interface SearchResult {
  id: number | string
  type: 'user' | 'order' | 'transaction' | 'content' | 'voucher'
  title: string
  subtitle?: string
  // For modal-based results, we store the numeric ID
  numericId?: number
}

export function GlobalSearch() {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  
  const { token } = useAuth()
  const navigate = useNavigate()
  const { openUserModal, openTransactionModal, openOrderModal } = useModal()
  const params = useParams({ strict: false })
  const eventId = (params as { eventId?: string }).eventId
  const eventIdNum = eventId ? Number(eventId) : null
  
  // Permission checks for search categories
  const canViewUsers = usePermission('can_view_users')
  const canViewOrders = usePermission('can_view_orders')
  const canViewTransactions = usePermission('can_view_transactions')
  const canViewContent = usePermission('can_view_event_content')
  const canViewVouchers = usePermission('can_view_vouchers')
  const canSeeUserInfo = usePermission('can_see_user_info')
  
  const { data: event } = useEvent(eventIdNum || 0)
  const isDirectPay = event?.event_type === 'direct_pay'
  const tokenValue = event?.token_value || 1
  const currency = event?.currency || 'USD'
  
  const formatAmount = (total: number) => {
    if (isDirectPay) {
      const dollarValue = total * tokenValue
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(dollarValue)
    }
    return `${total} tokens`
  }
  
  const formatUserName = (user: any) => {
    if (!user?.first_name) return user?.email || 'Unknown'
    if (canSeeUserInfo) {
      return `${user.first_name} ${user.last_name || ''}`.trim()
    }
    return `${user.first_name}${user.last_name ? ` ${user.last_name.charAt(0)}.` : ''}`
  }
  
  const debouncedQuery = useDebounce(query, 300)
  
  // Open search with / key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Open with / key (when not in an input)
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault()
        setIsOpen(true)
      }
      
      // Close with Escape
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false)
        setQuery('')
        setResults([])
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])
  
  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])
  
  // Search when query changes
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2 || !token || !eventIdNum) {
      setResults([])
      return
    }
    
    const search = async () => {
      setIsLoading(true)
      const searchResults: SearchResult[] = []
      
      try {
        // Only search categories the user has permission to view
        const promises: Promise<{ results?: any[] }>[] = []
        const promiseTypes: ('user' | 'order' | 'transaction' | 'content' | 'voucher')[] = []
        
        // Search users (if permitted)
        if (canViewUsers) {
          promises.push(
            apiClient.getEventUsers(token, eventIdNum, {
              search: debouncedQuery,
              page_size: 5,
            }).catch(() => ({ results: [] }))
          )
          promiseTypes.push('user')
        }
        
        // Search orders (if permitted)
        if (canViewOrders) {
          promises.push(
            apiClient.getOrders(token, eventIdNum, {
              search: debouncedQuery,
              page_size: 5,
            }).catch(() => ({ results: [] }))
          )
          promiseTypes.push('order')
        }
        
        // Search transactions (if permitted)
        if (canViewTransactions) {
          promises.push(
            apiClient.getTransactions(token, eventIdNum, {
              search: debouncedQuery,
              page_size: 5,
            }).catch(() => ({ results: [] }))
          )
          promiseTypes.push('transaction')
        }
        
        // Search event content (if permitted)
        if (canViewContent) {
          promises.push(
            apiClient.getContentPages(token, eventIdNum, {
              search: debouncedQuery,
              page_size: 5,
            }).catch(() => ({ results: [] }))
          )
          promiseTypes.push('content')
        }
        
        // Search vouchers (if permitted)
        if (canViewVouchers) {
          promises.push(
            apiClient.getVouchers(token, {
              event: eventIdNum,
              search: debouncedQuery,
              page_size: 5,
            }).catch(() => ({ results: [] }))
          )
          promiseTypes.push('voucher')
        }
        
        const results = await Promise.all(promises)
        
        // Process results based on their type
        results.forEach((data, index) => {
          const type = promiseTypes[index]
          
          if (type === 'user') {
            data.results?.forEach((user: any) => {
              searchResults.push({
                id: user.id,
                type: 'user',
                title: formatUserName(user),
                subtitle: canSeeUserInfo ? user.email : undefined,
                numericId: user.id,
              })
            })
          } else if (type === 'order') {
            data.results?.forEach((order: any) => {
              const userName = formatUserName(order.user)
              searchResults.push({
                id: order.id,
                type: 'order',
                title: `Order #${order.id}`,
                subtitle: `${userName} • ${formatAmount(order.total || order.total_amount || 0)}`,
                numericId: order.id,
              })
            })
          } else if (type === 'transaction') {
            data.results?.forEach((tx: any) => {
              const userName = formatUserName(tx.user)
              searchResults.push({
                id: tx.id,
                type: 'transaction',
                title: `Transaction #${tx.id}`,
                subtitle: `${tx.action || tx.type || 'Unknown'} • ${userName}`,
                numericId: tx.id,
              })
            })
          } else if (type === 'content') {
            data.results?.forEach((item: any) => {
              searchResults.push({
                id: item.id,
                type: 'content',
                title: item.title || item.name || `Content #${item.id}`,
                subtitle: item.type || 'Event Content',
                numericId: item.id,
              })
            })
          } else if (type === 'voucher') {
            data.results?.forEach((voucher: any) => {
              searchResults.push({
                id: voucher.id,
                type: 'voucher',
                title: voucher.code || `Voucher #${voucher.id}`,
                subtitle: `${voucher.amount || 0} tokens • ${voucher.status || 'Unknown'}`,
                numericId: voucher.id,
              })
            })
          }
        })
        
        setResults(searchResults)
        setSelectedIndex(0)
      } catch (error) {
        console.error('Search error:', error)
      } finally {
        setIsLoading(false)
      }
    }
    
    search()
  }, [debouncedQuery, token, eventIdNum, canViewUsers, canViewOrders, canViewTransactions, canViewContent, canViewVouchers])
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault()
      handleSelect(results[selectedIndex])
    }
  }, [results, selectedIndex])
  
  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && results.length > 0) {
      const selectedElement = resultsRef.current.children[selectedIndex] as HTMLElement
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [selectedIndex, results.length])
  
  const handleSelect = (result: SearchResult) => {
    if (!eventIdNum || !result.numericId) return
    
    // Open appropriate modal based on result type
    // Keep search open for modal-based results so user can return to search
    switch (result.type) {
      case 'user':
        openUserModal(result.numericId, eventIdNum)
        // Keep search open - modal will appear on top
        break
      case 'order':
        openOrderModal(result.numericId, eventIdNum)
        // Keep search open - modal will appear on top
        break
      case 'transaction':
        openTransactionModal(result.numericId, eventIdNum)
        // Keep search open - modal will appear on top
        break
      case 'content':
        // Navigate to content page (no modal for content) - close search
        setIsOpen(false)
        setQuery('')
        setResults([])
        navigate({ to: `/events/${eventIdNum}/content` })
        break
      case 'voucher':
        // Navigate to vouchers page (no modal for vouchers yet) - close search
        setIsOpen(false)
        setQuery('')
        setResults([])
        navigate({ to: `/events/${eventIdNum}/vouchers` })
        break
    }
  }
  
  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'user':
        return <User className="h-4 w-4" />
      case 'order':
        return <ShoppingCart className="h-4 w-4" />
      case 'transaction':
        return <CreditCard className="h-4 w-4" />
      case 'content':
        return <FileText className="h-4 w-4" />
      case 'voucher':
        return <Ticket className="h-4 w-4" />
    }
  }
  
  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'user': return 'User'
      case 'order': return 'Order'
      case 'transaction': return 'Transaction'
      case 'content': return 'Content'
      case 'voucher': return 'Voucher'
    }
  }
  
  const getTypeColor = (type: SearchResult['type']) => {
    switch (type) {
      case 'user': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      case 'order': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
      case 'transaction': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      case 'content': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
      case 'voucher': return 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400'
    }
  }

  // Don't show if no event is selected
  if (!eventIdNum) {
    return null
  }

  return (
    <>
      {/* Search Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg border border-neutral-200 dark:border-neutral-700 transition-colors"
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">Search...</span>
        <kbd className="hidden sm:inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium text-neutral-500 dark:text-neutral-400 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded">
          /
        </kbd>
      </button>
      
      {/* Search Modal - rendered via portal to escape stacking contexts */}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-[100]">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60"
            onClick={() => {
              setIsOpen(false)
              setQuery('')
              setResults([])
            }}
          />
          
          {/* Modal */}
          <div className="absolute inset-0 flex items-start justify-center pt-[15vh] px-4 pointer-events-none overflow-y-auto">
            <div className="relative w-full max-w-xl bg-white dark:bg-neutral-900 rounded-xl shadow-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden pointer-events-auto">
              {/* Search Input */}
              <div className="flex items-center px-4 border-b border-neutral-200 dark:border-neutral-800">
                <Search className="h-5 w-5 text-neutral-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search users, orders, transactions, content, vouchers..."
                  className="flex-1 px-3 py-4 text-sm sm:text-base bg-transparent text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none"
                />
                {isLoading && <Loader2 className="h-5 w-5 text-neutral-400 animate-spin" />}
                {query && !isLoading && (
                  <button
                    onClick={() => {
                      setQuery('')
                      setResults([])
                      inputRef.current?.focus()
                    }}
                    className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Results */}
              {results.length > 0 && (
                <div ref={resultsRef} className="max-h-[60vh] overflow-y-auto py-2">
                  {results.map((result, index) => (
                    <button
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleSelect(result)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                        index === selectedIndex
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : 'hover:bg-neutral-50 dark:hover:bg-neutral-800'
                      )}
                    >
                      <div className={cn(
                        'flex items-center justify-center w-8 h-8 rounded-lg',
                        getTypeColor(result.type)
                      )}>
                        {getIcon(result.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                            {result.title}
                          </span>
                          <span className={cn(
                            'px-1.5 py-0.5 text-[10px] font-medium rounded',
                            getTypeColor(result.type)
                          )}>
                            {getTypeLabel(result.type)}
                          </span>
                        </div>
                        {result.subtitle && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                            {result.subtitle}
                          </p>
                        )}
                      </div>
                      {index === selectedIndex && (
                        <kbd className="hidden sm:inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded">
                          Enter
                        </kbd>
                      )}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Empty State */}
              {query.length >= 2 && !isLoading && results.length === 0 && (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    No results found for "{query}"
                  </p>
                </div>
              )}
              
              {/* Initial State */}
              {query.length < 2 && (
                <div className="px-4 py-6">
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center mb-4">
                    Type at least 2 characters to search
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {canViewUsers && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded">
                        <User className="h-3 w-3" /> Users
                      </span>
                    )}
                    {canViewOrders && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded">
                        <ShoppingCart className="h-3 w-3" /> Orders
                      </span>
                    )}
                    {canViewTransactions && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded">
                        <CreditCard className="h-3 w-3" /> Transactions
                      </span>
                    )}
                    {canViewContent && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded">
                        <FileText className="h-3 w-3" /> Content
                      </span>
                    )}
                    {canViewVouchers && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded">
                        <Ticket className="h-3 w-3" /> Vouchers
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Footer */}
              <div className="flex items-center justify-between px-4 py-2 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950">
                <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded text-[10px]">↑</kbd>
                    <kbd className="px-1.5 py-0.5 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded text-[10px]">↓</kbd>
                    Navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded text-[10px]">Enter</kbd>
                    Select
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded text-[10px]">Esc</kbd>
                    Close
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
